import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import config from 'config';
import PropTypes from 'prop-types';

import { useNavElements } from '~/shared/utils/NavElements';
import { useBreakpoint } from '~/techstyle-shared/react-components';

const Context = createContext();

export const useGridScrollContext = () => useContext(Context);

export default function GridScrollContext({ children }) {
  const desktopRef = useRef();
  const mobileRef = useRef();
  const { isMobile } = useBreakpoint();
  const { headerAttachmentContainerRef } = useNavElements();

  const headerAttachmentHeight =
    headerAttachmentContainerRef.current?.clientHeight || 0;

  const handleSetScroll = useCallback(() => {
    const scrollToTopOfGrid = config.get('public.brand.scrollToTopOfGrid');
    const refToScroll = isMobile ? mobileRef : desktopRef;

    if (document.activeElement) {
      document.activeElement.blur();
    }

    if (scrollToTopOfGrid) {
      document.body.scrollIntoView({
        behavior: 'smooth',
      });
    } else {
      if (refToScroll.current) {
        window.scrollTo({
          top: refToScroll.current.offsetTop + headerAttachmentHeight, // height of fixed nav bar + padding
          behavior: 'smooth',
        });
      }
    }
  }, [isMobile, headerAttachmentHeight]);

  const value = useMemo(
    () => ({
      handleSetScroll,
      desktopRef,
      mobileRef,
    }),
    [handleSetScroll, desktopRef, mobileRef]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

GridScrollContext.propTypes = {
  children: PropTypes.node,
};

GridScrollContext.Context = Context;
